/* Filter each country picker locally; Shopify still handles the actual selection. */
(() => {
  if (customElements.get('ac-country-search')) return;
  const normalize = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const aliases = { US: 'USA America', GB: 'UK Britain', AE: 'UAE', HK: 'Hongkong' };
  const displayNames = [];
  if (typeof Intl.DisplayNames === 'function') {
    for (const locale of ['en', 'zh-Hans']) {
      try { displayNames.push(new Intl.DisplayNames([locale], {type: 'region'})); } catch {}
    }
  }
  customElements.define('ac-country-search', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.input = this.querySelector('[data-country-search-input]');
      this.list = this.querySelector('.ac-country-search__results');
      this.empty = this.querySelector('.ac-country-search__empty');
      if (!this.input || !this.list || !this.empty) return;
      this.abort = new AbortController();
      const field = this.querySelector('.ac-country-search__field');
      if (field.hidden) field.hidden = false;
      this.input.addEventListener('input', () => this.filterCountries(), {signal: this.abort.signal});
      this.input.addEventListener('search', () => this.filterCountries(), {signal: this.abort.signal});
      // Capture arrow keys before Impact's unfiltered sibling navigation sees them.
      this.addEventListener('keydown', event => this.onKeydown(event), {capture: true, signal: this.abort.signal});
      this.countryPopover = this.closest('.ac-country-popover');
      this.mobileViewport = window.matchMedia('(max-width: 999px)');
      const scheduleViewport = () => this.scheduleViewport();
      const viewportEvents = {passive: true, signal: this.abort.signal};
      window.visualViewport?.addEventListener('resize', scheduleViewport, viewportEvents);
      window.visualViewport?.addEventListener('scroll', scheduleViewport, viewportEvents);
      window.addEventListener('resize', scheduleViewport, viewportEvents);
      this.input.addEventListener('focus', scheduleViewport, {signal: this.abort.signal});
      this.countryPopover?.addEventListener('toggle', event => {
        if (event.target !== this.countryPopover) return;
        if (this.countryPopover.open) this.filterCountries();
        this.updateViewport();
      }, {signal: this.abort.signal});
      this.countryPopover?.addEventListener('dialog:after-show', scheduleViewport, {signal: this.abort.signal});
      this.countryPopover?.addEventListener('dialog:after-hide', () => this.clearViewport(), {signal: this.abort.signal});
      // Impact reparents the whole navigation drawer on every open/close.
      // Do not initialize its hidden country picker on that interaction path.
      if (this.countryPopover?.open) this.filterCountries();
      this.updateViewport();
    }
    ensureRecords() {
      // Reuse the index when the same picker moves between header and body.
      if (this.indexedList === this.list) return;
      this.records = Array.from(this.list.querySelectorAll('button[name="country_code"]')).map(button => {
        const code = button.value;
        const translated = displayNames.map(names => { try { return names.of(code); } catch { return ''; } });
        return { button, terms: normalize([code, button.dataset.countrySearchTerms, aliases[code], ...translated].filter(Boolean).join(' ')) };
      });
      this.indexedList = this.list;
    }
    filterCountries() {
      this.ensureRecords();
      const words = normalize(this.input.value).split(/\s+/).filter(Boolean);
      const shortcut = {usa: 'us', uk: 'gb', uae: 'ae'}[words[0]] || words[0];
      const exactCountry = words.length === 1 ? this.records.find(record => record.button.value.toLowerCase() === shortcut) : null;
      this.visible = [];
      for (const record of this.records) {
        const searchable = record.terms + ' ' + normalize(record.button.textContent);
        const matches = exactCountry ? record === exactCountry : words.every(word => searchable.includes(word));
        if (record.button.hidden !== !matches) record.button.hidden = !matches;
        if (matches) this.visible.push(record.button);
      }
      this.empty.hidden = this.visible.length !== 0;
      this.list.hidden = this.visible.length === 0;
      this.list.scrollTop = 0;
    }
    scheduleViewport() {
      if (this.viewportFrame) return;
      this.viewportFrame = requestAnimationFrame(() => {
        this.viewportFrame = null;
        this.updateViewport();
      });
    }
    updateViewport() {
      if (!this.countryPopover || !this.isConnected) return;
      if (!this.mobileViewport.matches) {
        this.clearViewport();
        return;
      }
      // Keep the last geometry during closing; the native dialog owns that animation.
      if (!this.countryPopover.open) return;
      const viewport = window.visualViewport;
      // Pinch zoom is a user action, not keyboard occlusion: do not chase it.
      if (viewport && viewport.scale > 1.05) return;
      const height = viewport?.height || window.innerHeight;
      const top = viewport?.offsetTop || 0;
      // innerHeight also accounts for a narrow page's automatic viewport scaling.
      const layoutHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
      const bottom = Math.max(0, layoutHeight - top - height);
      this.countryPopover.style.setProperty('--ac-country-visible-height', `${Math.round(height)}px`);
      this.countryPopover.style.setProperty('--ac-country-visible-bottom', `${Math.round(bottom)}px`);
      this.countryPopover.setAttribute('data-ac-country-viewport', '');
    }
    clearViewport() {
      this.countryPopover?.removeAttribute('data-ac-country-viewport');
      this.countryPopover?.style.removeProperty('--ac-country-visible-height');
      this.countryPopover?.style.removeProperty('--ac-country-visible-bottom');
    }
    onKeydown(event) {
      if (event.isComposing) return;
      if (!this.visible || this.indexedList !== this.list) this.filterCountries();
      if (event.target === this.input) {
        if (event.key === 'Escape' && this.input.value) {
          event.preventDefault(); event.stopPropagation();
          this.input.value = ''; this.filterCountries();
        } else if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
          event.preventDefault(); event.stopPropagation();
          const option = event.key === 'ArrowUp' ? this.visible.at(-1) : this.visible[0];
          option?.focus();
        }
        return;
      }
      const index = this.visible.indexOf(event.target);
      if (index < 0 || !['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault(); event.stopPropagation();
      let next = index;
      if (event.key === 'ArrowDown') next = (index + 1) % this.visible.length;
      if (event.key === 'ArrowUp') next = (index - 1 + this.visible.length) % this.visible.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.visible.length - 1;
      this.visible[next]?.focus();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = null;
      this.clearViewport();
    }
  });
})();

